import {
  accessSentence,
  accessSentenceFormatter,
} from '@affine/core/modules/streamlain/app-access/access-sentence';
import { I18n } from '@affine/i18n';
import type {
  GoalTreeEnvelope,
  GoalTreeModelTier,
  GoalTreeNode,
  GoalTreeNodeStatus,
  GoalTreeReasoningLevel,
} from '@streamlain/view-config';
import {
  APP_ACCESS_REVIEW_NODE_ID,
  readAppAccessReviewPage,
} from '@streamlain/view-config';

import { isGoalBusy } from './goal-run-status';

export interface GoalTreeViewNode {
  readonly node: GoalTreeNode;
  readonly nodeId: string;
  readonly label: string;
  readonly subtitle: string;
  readonly statusLabel: string;
  readonly statusTone: string;
  readonly ariaLabel: string;
  readonly modelLabel: string;
  readonly editable: boolean;
  readonly active: boolean;
  readonly depth: number;
  readonly hasChildren: boolean;
  readonly expanded: boolean;
  readonly treeGuideDepths: number[];
  readonly treeLast: boolean;
}

export interface GoalTreeViewModel {
  readonly title: string;
  readonly subtitle: string;
  readonly progressLabel: string;
  /** Share of the goal's steps that are done, 0 to 1. */
  readonly progressRatio: number;
  readonly nodes: readonly GoalTreeViewNode[];
  readonly gapsBlocking: number;
  readonly gapsWarning: number;
}

export function createGoalTreeViewModel(input: {
  readonly tree: GoalTreeEnvelope;
  readonly expandedNodeIds: ReadonlySet<string>;
  readonly activeNodeId?: string | null;
}): GoalTreeViewModel {
  const nodes: GoalTreeViewNode[] = [];
  collectNodes({
    node: input.tree.root,
    depth: 0,
    expandedNodeIds: input.expandedNodeIds,
    nodes,
    treeGuideDepths: [],
    treeLast: true,
    activeNodeId: input.activeNodeId ?? null,
  });
  return {
    title: input.tree.root.label,
    subtitle: goalModeLabelForDisplay(input.tree.domain, input.tree.mode),
    progressLabel: progressLabel(input.tree),
    progressRatio: progressRatio(input.tree),
    nodes,
    gapsBlocking: input.tree.gaps.filter(gap => gap.blocking).length,
    gapsWarning: input.tree.gaps.filter(gap => !gap.blocking).length,
  };
}

export function effectiveStepModelLabel(
  tier: GoalTreeModelTier | undefined,
  reasoning: GoalTreeReasoningLevel | undefined
): string {
  const parts = [
    tier ? modelTierLabel(tier) : null,
    reasoning && reasoning !== 'off'
      ? I18n.t('com.streamlain.ai.target.model.reasoning', {
          level: reasoningLabel(reasoning),
        })
      : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

export function modelTierLabel(tier: GoalTreeModelTier): string {
  return {
    economy: I18n.t('com.streamlain.ai.target.model.economy'),
    standard: I18n.t('com.streamlain.ai.target.model.standard'),
    pro: I18n.t('com.streamlain.ai.target.model.pro'),
  }[tier];
}

export function goalStatusLabel(status: GoalTreeNodeStatus | string): string {
  const labels: Record<string, string> = {
    blocked: I18n.t('com.streamlain.ai.target.status.blocked'),
    completed: I18n.t('com.streamlain.ai.target.status.completed'),
    configured: I18n.t('com.streamlain.ai.target.status.configured'),
    defaulted: I18n.t('com.streamlain.ai.target.status.prepared'),
    invalid: I18n.t('com.streamlain.ai.target.status.invalid'),
    missing: I18n.t('com.streamlain.ai.target.status.missing'),
    ready: I18n.t('com.streamlain.ai.target.status.ready'),
    running: I18n.t('com.streamlain.ai.target.status.running'),
  };
  return labels[status] ?? I18n.t('com.streamlain.ai.target.status.open');
}

export function goalNodeLabel(node: GoalTreeNode): string {
  const labels: Record<string, string> = {
    'appBase.title': I18n.t('com.streamlain.ai.target.node.app-title'),
    'appBase.brief': I18n.t('com.streamlain.ai.target.node.app-brief'),
    'appBase.slug': I18n.t('com.streamlain.ai.target.node.app-address'),
    'appBase.exposure': I18n.t('com.streamlain.ai.target.node.visibility'),
    'appBase.exposure.delivery': I18n.t(
      'com.streamlain.ai.target.node.delivery'
    ),
    'appBase.exposure.auth': I18n.t('com.streamlain.ai.target.node.access'),
    'appBase.exposure.cache': I18n.t(
      'com.streamlain.ai.target.node.performance'
    ),
    'appBase.exposure.robots': I18n.t(
      'com.streamlain.ai.target.node.discovery'
    ),
    'appBase.shell': I18n.t('com.streamlain.ai.target.node.structure'),
    'appBase.composition': I18n.t('com.streamlain.ai.target.node.composition'),
    'appBase.mount': I18n.t('com.streamlain.ai.target.node.main-view'),
    'appBase.route': I18n.t('com.streamlain.ai.target.node.start-page'),
    'appBase.file': I18n.t('com.streamlain.ai.target.node.interface'),
    'appBase.file.html': I18n.t('com.streamlain.ai.target.node.layout'),
    'appBase.file.css': I18n.t('com.streamlain.ai.target.node.design'),
    'appBase.file.js': I18n.t('com.streamlain.ai.target.node.behavior'),
    'appBase.app': I18n.t('com.streamlain.ai.target.node.app-view'),
    'appBase.database.requirement': I18n.t(
      'com.streamlain.ai.target.node.data'
    ),
    'appBase.resource': I18n.t('com.streamlain.ai.target.node.data-source'),
    'appBase.column.requirement': I18n.t(
      'com.streamlain.ai.target.node.data-fields'
    ),
    'appBase.database.binding': I18n.t(
      'com.streamlain.ai.target.node.database'
    ),
    'appBase.automation.binding': I18n.t(
      'com.streamlain.ai.target.node.automation'
    ),
    'appBase.command': I18n.t('com.streamlain.ai.target.node.actions'),
    'appBase.export': I18n.t('com.streamlain.ai.target.node.export'),
    'appBase.seo': I18n.t('com.streamlain.ai.target.node.findability'),
    'appBase.operator.controls': I18n.t(
      'com.streamlain.ai.target.node.ai-controls'
    ),
    'appBase.health': I18n.t('com.streamlain.ai.target.node.quality'),
    'appBase.health.config': I18n.t(
      'com.streamlain.ai.target.node.configuration'
    ),
    'appBase.health.composition': I18n.t(
      'com.streamlain.ai.target.node.view-structure'
    ),
    'appBase.health.mainMount': I18n.t(
      'com.streamlain.ai.target.node.main-view-check'
    ),
    'appBase.health.artifact': I18n.t(
      'com.streamlain.ai.target.node.app-files'
    ),
    'appBase.health.databaseSchema': I18n.t(
      'com.streamlain.ai.target.node.data-structure'
    ),
    'appBase.health.databasePresentation': I18n.t(
      'com.streamlain.ai.target.node.data-view'
    ),
    'appBase.health.dataBinding': I18n.t(
      'com.streamlain.ai.target.node.data-connection'
    ),
    'appBase.health.interaction': I18n.t(
      'com.streamlain.ai.target.node.interaction'
    ),
    'appBase.health.live': I18n.t('com.streamlain.ai.target.node.publication'),
    'appBase.health.launch': I18n.t('com.streamlain.ai.target.node.launch'),
    'appBase.publish': I18n.t('com.streamlain.ai.target.node.publish'),
    [APP_ACCESS_REVIEW_NODE_ID]: I18n.t(
      'com.streamlain.ai.target.node.data-access'
    ),
  };
  return labels[node.nodeId] ?? node.label;
}

export function resolveActiveGoalNodeId(
  tree: GoalTreeEnvelope,
  goalStatus?: string | null
): string | null {
  if (!isGoalBusy(goalStatus)) return null;
  return tree.nextSteps[0]?.nodeId ?? tree.root.nodeId;
}

export function goalActivityText(input: {
  tree: GoalTreeEnvelope;
  status?: string | null;
}): string | null {
  const activeNodeId = resolveActiveGoalNodeId(input.tree, input.status);
  const activeNode = activeNodeId
    ? findNode(input.tree.root, activeNodeId)
    : null;
  if (input.status === 'queued') {
    return I18n.t('com.streamlain.ai.target.activity.preparing');
  }
  if (isGoalBusy(input.status)) {
    return I18n.t('com.streamlain.ai.target.activity.working', {
      step: activeNode ? goalNodeLabel(activeNode) : input.tree.root.label,
    });
  }
  if (input.status === 'waiting_for_confirmation') {
    return I18n.t('com.streamlain.ai.target.activity.confirmation');
  }
  if (input.status === 'waiting_for_user') {
    return I18n.t('com.streamlain.ai.target.activity.input');
  }
  if (input.status === 'paused') {
    return I18n.t('com.streamlain.ai.target.activity.paused');
  }
  return null;
}

export function nodeStatusTone(status: GoalTreeNodeStatus) {
  if (status === 'completed' || status === 'configured') return 'positive';
  if (status === 'invalid') return 'critical';
  if (status === 'blocked') return 'blocked';
  if (status === 'missing') return 'warning';
  if (status === 'running') return 'accent';
  if (status === 'defaulted') return 'muted';
  return 'neutral';
}

function collectNodes(input: {
  readonly node: GoalTreeNode;
  readonly depth: number;
  readonly expandedNodeIds: ReadonlySet<string>;
  readonly nodes: GoalTreeViewNode[];
  readonly treeGuideDepths: readonly number[];
  readonly treeLast: boolean;
  readonly activeNodeId: string | null;
}): void {
  const hasChildren = input.node.children.length > 0;
  const expanded =
    !hasChildren ||
    input.depth < 1 ||
    input.expandedNodeIds.has(input.node.nodeId);
  input.nodes.push({
    node: input.node,
    nodeId: input.node.nodeId,
    label: goalNodeLabel(input.node),
    subtitle: goalNodeSubtitle(input.node),
    statusLabel: goalStatusLabel(input.node.status),
    statusTone: nodeStatusTone(input.node.status),
    ariaLabel: `${goalNodeLabel(input.node)}, ${goalStatusLabel(input.node.status)}`,
    modelLabel: effectiveStepModelLabel(
      input.node.modelPolicy?.userOverride?.tier ??
        input.node.modelPolicy?.defaultTier,
      input.node.modelPolicy?.userOverride?.reasoning ??
        input.node.modelPolicy?.reasoning
    ),
    editable:
      input.node.required &&
      (input.node.status === 'missing' ||
        input.node.status === 'invalid' ||
        input.node.status === 'blocked'),
    active: input.node.nodeId === input.activeNodeId,
    depth: input.depth,
    hasChildren,
    expanded,
    treeGuideDepths: [...input.treeGuideDepths],
    treeLast: input.treeLast,
  });
  if (!expanded) return;
  input.node.children.forEach((child, index, siblings) => {
    collectNodes({
      node: child,
      depth: input.depth + 1,
      expandedNodeIds: input.expandedNodeIds,
      nodes: input.nodes,
      treeGuideDepths:
        input.treeLast || input.depth === 0
          ? input.treeGuideDepths
          : [...input.treeGuideDepths, input.depth],
      treeLast: index === siblings.length - 1,
      activeNodeId: input.activeNodeId,
    });
  });
}

function goalNodeSubtitle(node: GoalTreeNode): string {
  if (
    (node.nodeId === 'appBase.title' || node.nodeId === 'appBase.brief') &&
    node.valueSummary
  ) {
    return node.valueSummary;
  }
  return accessReviewPageSentence(node) ?? goalStatusLabel(node.status);
}

/**
 * A page at "Data & access": who may do what there after "Apply all", in
 * the sentence the Studio shows for the page.
 */
function accessReviewPageSentence(node: GoalTreeNode): string | null {
  const page = readAppAccessReviewPage(node);
  return page
    ? accessSentenceFormatter(I18n)(accessSentence(page.recommended))
    : null;
}

export function goalModeLabelForDisplay(domain: string, mode: string): string {
  if (domain === 'app_base') {
    return mode === 'create_app_base_from_scratch'
      ? I18n.t('com.streamlain.ai.target.mode.create-app')
      : I18n.t('com.streamlain.ai.target.mode.edit-app');
  }
  if (domain === 'automation') {
    return I18n.t('com.streamlain.ai.target.mode.automation');
  }
  return I18n.t('com.streamlain.ai.target.mode.general');
}

export function reasoningLabel(reasoning: GoalTreeReasoningLevel): string {
  return {
    off: I18n.t('com.streamlain.ai.target.reasoning.off'),
    low: I18n.t('com.streamlain.ai.target.reasoning.low'),
    medium: I18n.t('com.streamlain.ai.target.reasoning.medium'),
    high: I18n.t('com.streamlain.ai.target.reasoning.high'),
  }[reasoning];
}

function findNode(node: GoalTreeNode, nodeId: string): GoalTreeNode | null {
  if (node.nodeId === nodeId) return node;
  for (const child of node.children) {
    const match = findNode(child, nodeId);
    if (match) return match;
  }
  return null;
}

function progressDone(tree: GoalTreeEnvelope): number {
  return (
    (tree.progress.completed ?? 0) +
    tree.progress.ready +
    tree.progress.configured +
    tree.progress.defaulted
  );
}

function progressLabel(tree: GoalTreeEnvelope): string {
  return `${progressDone(tree)}/${tree.progress.total}`;
}

function progressRatio(tree: GoalTreeEnvelope): number {
  const { total } = tree.progress;
  return total > 0 ? Math.min(1, progressDone(tree) / total) : 0;
}
